import { FormInstance } from 'antd';
import dayjs from 'dayjs';
import appointmentApis from 'features/appointment/services/apis';
import { aptUIActions } from 'features/appointment/services/reducers';
import { IBodyApiBookingMultiple, IBodyApiBookingMultipleItem } from 'features/appointment/services/types/api';
import { IAppointmentItemData } from 'features/appointment/services/types/appointment';
import { DATE_FORMAT } from 'features/appointment/utils/format';
import { get, uniqueId } from 'lodash';
import { useMemo } from 'react';
import { IResponseDataBody } from 'services/response';
import { IServiceItemData } from 'services/shop/types/categories';
import { IStaffItemData } from 'services/shop/types/staff';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { useAppDispatch } from 'store/hooks';
import toast from 'utils/toast';
import { ModalEditStartTimeRef } from './ModalEditStartTime';
import { ModalServicePickerRef } from './ModalServicesPicker';
import { ModalStaffPickerRef } from './ModalStaffPicker';
import { ModalTimePickerRef } from './ModalTimePicker';
import { BookingV4Actions } from './context';
import {
  fromDisplayTime,
  getCurrentDuration,
  getCurrentStartTime,
  getStaffStartTime,
  resequenceServices,
  toDisplayTime,
} from './StaffServiceSection/helpers';
import { IBookingV4FormValues, IBookingV4ServiceItem, IBookingV4StaffItem } from './types';
import ModalWarning from 'widgets/ModalWarning';

const toStaffItem = (staff: IStaffItemData): IBookingV4StaffItem => ({
  id: uniqueId('staff_'),
  staffId: staff.id,
  staffName: staff.nickName || staff.firstName,
  staffAvatar: staff.urlImage,
  services: [],
  requestStaff: false,
  anybody: staff.anyBody,
});

const toServiceItem = (service: IServiceItemData, staffId: string): IBookingV4ServiceItem => ({
  id: uniqueId('service_'),
  serviceId: service.id,
  serviceName: service.serviceName,
  startTime: '',
  staffId,
  duration: service.duration,
});

type UseBookingActions = {
  actions: BookingV4Actions;
  submit: (values: IBookingV4FormValues) => Promise<void>;
};

export const useBookingActions = (form: FormInstance, onClose: () => void): UseBookingActions => {
  const dispatch = useAppDispatch();
  const setLoading = useSetLoadingPage();

  return useMemo(() => {
    const getDateParam = () => {
      const date = form.getFieldValue('date');
      return date ? dayjs(date).format(DATE_FORMAT) : '';
    };
    const getStaffs = (): IBookingV4StaffItem[] => form.getFieldValue('staffs') || [];
    const setStaffs = (next: IBookingV4StaffItem[]) => form.setFieldValue('staffs', next);
    const getBlock = (id: string) => getStaffs().find((b) => b.id === id);
    const updateStaff = (id: string, updater: (item: IBookingV4StaffItem) => IBookingV4StaffItem) =>
      setStaffs(getStaffs().map((item) => (item.id === id ? updater(item) : item)));

    // Add staff -> pick start time -> pick services. The empty start-time state
    // loops back to staff selection, so this is a loop. Added staff defaults to requestStaff.
    const addNewStaff = async () => {
      const date = getDateParam();
      for (; ;) {
        const staff = await ModalStaffPickerRef.current?.open({
          date,
          excludeStaffIds: getStaffs().map((b) => b.staffId),
        });
        if (!staff) return; // cancel at staff picker -> abort
        const r = await ModalTimePickerRef.current?.open({ staffId: staff.id, date, allowChangeStaff: true });
        if (!r || r.type === 'cancel') continue; // abort
        if (r.type === 'changeStaff') continue; // empty/back -> reopen staff picker
        const services = await ModalServicePickerRef.current?.open(staff.id);
        if (!services?.length) return; // abort
        const block: IBookingV4StaffItem = { ...toStaffItem(staff), requestStaff: true };
        block.services = resequenceServices(services.map((s) => toServiceItem(s, staff.id)), r.value);
        setStaffs([...getStaffs(), block]);
        return;
      }
    };

    const changeStaff = async (blockId: string) => {
      const block = getBlock(blockId);
      if (!block) return;
      const staff = await ModalStaffPickerRef.current?.open({
        date: getDateParam(),
        activeStaffId: block.staffId,
        excludeStaffIds: getStaffs().filter((b) => b.id !== blockId).map((b) => b.staffId),
      });
      if (!staff) return;
      updateStaff(blockId, (item) => ({
        ...item,
        staffId: staff.id,
        staffName: staff.nickName || staff.firstName,
        staffAvatar: staff.urlImage,
        anybody: staff.anyBody,
      }));
    };

    const pickStaffTime = async (blockId: string) => {
      const block = getBlock(blockId);
      if (!block) return;
      const r = await ModalTimePickerRef.current?.open({
        staffId: block.staffId,
        date: getDateParam(),
        defaultValue: getStaffStartTime(block),
      });
      if (!r || r.type !== 'select') return;
      updateStaff(blockId, (item) => ({ ...item, services: resequenceServices(item.services, r.value) }));
    };

    const addNewService = async (blockId: string) => {
      const block = getBlock(blockId);
      if (!block) return;
      const services = await ModalServicePickerRef.current?.open(block.staffId);
      if (!services?.length) return;
      const newItems = services.map((s) => toServiceItem(s, block.staffId));
      updateStaff(blockId, (item) => {
        const merged = [...item.services, ...newItems];
        const start = getStaffStartTime(item);
        return { ...item, services: start ? resequenceServices(merged, start) : merged };
      });
    };

    // Edit a service's start time / duration -> store as master* overrides.
    const editStartTime = async (blockId: string, service: IBookingV4ServiceItem) => {
      const block = getBlock(blockId);
      if (!block) return;
      const res = await ModalEditStartTimeRef.current?.open({
        staffId: block.staffId,
        date: getDateParam(),
        serviceName: service.serviceName,
        startTime: toDisplayTime(getCurrentStartTime(service)),
        duration: getCurrentDuration(service),
      });
      if (!res?.startTime) return;
      const newStart = fromDisplayTime(res.startTime);
      if (!newStart) return;
      updateStaff(blockId, (item) => ({
        ...item,
        services: item.services.map((s) =>
          s.id === service.id
            ? { ...s, masterStartTime: newStart, masterDuration: res.duration ?? s.masterDuration }
            : s
        ),
      }));
    };

    const toggleRequest = (blockId: string) =>
      updateStaff(blockId, (item) => ({ ...item, requestStaff: !item.requestStaff }));

    const removeStaff = (blockId: string) => setStaffs(getStaffs().filter((b) => b.id !== blockId));

    const removeService = (blockId: string, serviceId: string) =>
      updateStaff(blockId, (item) => {
        const services = item.services.filter((s) => s.id !== serviceId);
        const start = getStaffStartTime({ ...item, services });
        return { ...item, services: start ? resequenceServices(services, start) : services };
      });

    const submit = async (values: IBookingV4FormValues) => {
      const error = validateBooking(values);
      if (error) return toast.error(error);

      const dateStr = dayjs(values.date).format('MM/DD/YYYY');
      const items: IBodyApiBookingMultipleItem[] = [];
      values.staffs.forEach((block) =>
        block.services.forEach((svc) => {
          const startTime = `${dateStr} ${getCurrentStartTime(svc)}`;
          const endTime = dayjs(startTime, 'MM/DD/YYYY HH:mm')
            .add(getCurrentDuration(svc), 'minute')
            .format('MM/DD/YYYY HH:mm');
          items.push({
            staffId: block.staffId,
            serviceId: svc.serviceId,
            requestStaff: block.requestStaff,
            startTime,
            endTime,
            customerName: values.customer?.name || '',
            tempCustomerId: values.customer?.id || '',
          });
        })
      );

      const body: IBodyApiBookingMultiple = {
        createdBy: '',
        customerId: values.customer?.id || '',
        customerPhone: values.customer?.phone || '',
        customerName: values.customer?.name || '',
        numberCustomers: values.groupNumber || 1,
        note: values.note || '',
        repeat: 0,
        bookingType: 'CALL_IN',
        items,
      };

      setLoading(true);
      try {
        const res: IResponseDataBody<IAppointmentItemData[]> = await appointmentApis.bookingMultiple(body);
        if (res.data.data) {
          toast.success('Booking created successfully');
          form.resetFields();
          dispatch(aptUIActions.syncAppointments(res.data.data));
          onClose();
        }
      } catch (error) {
        const message = get(error, 'response.data.message') || '';
        ModalWarning.open({ message });
      } finally {
        setLoading(false);
      }
    };

    const actions: BookingV4Actions = {
      addNewStaff,
      changeStaff,
      pickStaffTime,
      toggleRequest,
      removeStaff,
      addNewService,
      removeService,
      editStartTime,
    };
    return { actions, submit };
  }, [form, dispatch, setLoading, onClose]);
};

// Returns the first validation error message, or '' when valid.
const validateBooking = (values: IBookingV4FormValues): string => {
  if (!values.customer) return 'Please select a customer';
  if (!values.staffs?.length) return 'Please add at least one staff';
  if (values.staffs.some((s) => !s.services.length)) return 'Each staff needs at least one service';
  if (values.staffs.some((s) => s.services.some((svc) => !getCurrentStartTime(svc))))
    return 'Each service needs a start time';
  return '';
};
